import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['admin', 'member', 'viewer'];

export default function CreateUserModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'member'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-user-modal">
      <div className="bg-white w-full max-w-md border-2 border-black">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="font-manrope font-bold text-2xl text-text-primary">Create New User</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="btn-close-create-user">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Full Name *</label>
            <input
              type="text"
              required
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
              placeholder="John Doe"
              data-testid="input-user-full-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
              placeholder="user@company.com"
              data-testid="input-user-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Password *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary pr-12"
                placeholder="••••••••"
                data-testid="input-user-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                data-testid="btn-toggle-user-password"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
              data-testid="select-user-role"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.toUpperCase()}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-2">
              {formData.role === 'admin' && 'Full access to all features'}
              {formData.role === 'member' && 'Can create and manage own credentials'}
              {formData.role === 'viewer' && 'Read-only access to shared credentials'}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="btn-submit-create-user"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
            <button type="button" onClick={onClose} className="btn-outline flex-1" data-testid="btn-cancel-create-user">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}